import type { Breadcrumb, Event, EventHint, Log } from '@sentry/nextjs';
import {
  FETCH_NO_RESPONSE,
  FETCH_NO_RESPONSE_MESSAGES,
  fetchNoResponseContext,
  reportedFetchNoResponseErrors,
} from './fetch-no-response';
import { redactQueryValues } from './span-policy';
import { staticAssetPath } from './static-asset-path';

/**
 * What every runtime strips from a Sentry event before it leaves the process.
 *
 * Sentry's server-side scrubbing reads structured keys and a few patterns
 * (email, card, SSN). It does not read free text, and every channel below
 * carries free text a recruiter's screen put there:
 *
 * - A console breadcrumb holds the whole logged line. Every structured server
 *   log line goes through `console`.
 * - A click breadcrumb names the element by its `aria-label`, `title` and
 *   `alt`. Applicant cards carry the applicant's name in `aria-label`.
 * - A URL carries its query string, and a search term is a candidate's name.
 *   The page URL, a request error's path, a navigation, a fetch, a span and a
 *   replay's page list all hold one. Parameter names survive, as
 *   `?query=[redacted]`, so an event still says which search failed. The
 *   path, its ids and any record-id or pagination value survive too: they are
 *   how support finds the record an event is about.
 * - An error message can quote an upstream response, and a 422 echoes the
 *   applicant fields it rejected. The server log funnel sends that message as
 *   the exception value, as an `extra` or as a log attribute, so each loses
 *   everything from ` Body: ` on, and any URL in it keeps its query keys only.
 *   An HTML document at the start of the text, an error page quoted whole, becomes
 *   `[HTML response body]`.
 * - An error or log message from monolith or WX can name a user or an
 *   applicant by email address. Each address in that free text becomes
 *   `[email]`.
 * - A request's headers carry credentials: the session cookie, and the
 *   `Authorization: Bearer` admin key Hire sends to `/api/v1/admin/*`. Only
 *   the headers named in `KEPT_HEADERS` leave the process, on the event's
 *   request and as span attributes alike.
 *
 * Pure, and free of the SDK at runtime, so the browser bundle can import it.
 */

const LABEL_ATTRIBUTES = /\[(aria-label|title|alt)="[^"]*"\]/g;

const RECORD_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Pagination and ordering: what a list read looked like, never who it found. */
const LIST_SHAPE_PARAMS = new Set([
  'page',
  'per_page',
  'perPage',
  'per_stage',
  'limit',
  'sort',
  'sortDirection',
  'refresh',
]);
const SHORT_TOKEN = /^[\w.-]{1,40}$/;

function decoded(part: string): string {
  try {
    return decodeURIComponent(part.replace(/\+/g, ' '));
  } catch {
    return part;
  }
}

/**
 * The query values support needs to find a record, and that cannot carry a
 * person: a record id, which names a record the way a path id does, and the
 * pagination and ordering of a list read. Every other value is free text until
 * proven otherwise, so a new parameter is private the day someone adds it.
 */
function isTroubleshootingValue(name: string, value: string): boolean {
  const plain = decoded(value);
  if (RECORD_ID.test(plain)) {
    return true;
  }
  const param = decoded(name).replace(/\[\]$/, '');
  return LIST_SHAPE_PARAMS.has(param) && SHORT_TOKEN.test(plain);
}

/** A URL with its path and parameter names intact and its free text gone. */
export function redactUrl(url: string): string {
  return redactQueryValues(url, isTroubleshootingValue);
}

/** Keys that hold a URL or a path with its query string. */
const URL_KEYS = [
  'url',
  'from',
  'to',
  'previous',
  'http.url',
  'http.target',
  'url.full',
  // Next's own name for a span, such as `fetch GET <url>`.
  'next.span_name',
];

/** Keys that hold a bare query string, with or without its `?`. */
const QUERY_KEYS = ['http.query', 'url.query'];

function redactQuery(query: string): string {
  return query.startsWith('?')
    ? redactUrl(query)
    : redactUrl(`?${query}`).slice(1);
}

/** Redacts query values in the URL-bearing fields of a breadcrumb or span. */
export function redactUrlFields<T extends Record<string, unknown>>(data: T): T {
  const scrubbed: Record<string, unknown> = { ...data };
  for (const key of URL_KEYS) {
    const value = scrubbed[key];
    if (typeof value === 'string') {
      scrubbed[key] = redactUrl(value);
    }
  }
  for (const key of QUERY_KEYS) {
    const value = scrubbed[key];
    if (typeof value === 'string') {
      scrubbed[key] = redactQuery(value);
    }
  }
  return scrubbed as T;
}

/**
 * The request headers an event keeps. An allowlist, so a credential header
 * nobody thought of (`Authorization`, `Proxy-Authorization`, an API key) is
 * dropped the day a caller starts sending it.
 */
const KEPT_HEADERS = new Set([
  'accept',
  'accept-language',
  'content-length',
  'content-type',
  'host',
  'referer',
  'user-agent',
]);

/**
 * A request or response header as a span attribute:
 * `http.request.header.<name>`, the name lower-cased with `-` as `_`, and
 * `http.request.header.cookie.<cookie>` for each cookie. With tracing on,
 * `@sentry/nextjs` copies the incoming request's headers onto the root span
 * this way, cookies included.
 */
const HEADER_ATTRIBUTE = /^http\.(?:request|response)\.header\.(.+)$/;
const KEPT_HEADER_ATTRIBUTES = new Set(
  [...KEPT_HEADERS].map((name) => name.replace(/-/g, '_'))
);
/**
 * The visitor's IP. `@sentry/nextjs` sets `http.client_ip` on every root span
 * from `X-Forwarded-For`, and `sendDefaultPii: false` leaves it there.
 */
const CLIENT_ADDRESS_ATTRIBUTES = new Set(['http.client_ip', 'client.address']);

/**
 * A span's attributes with the client IP and every header but those in
 * `KEPT_HEADERS` dropped, and query values redacted. The cookie attributes
 * carry the session and the impersonation JWT, which names both users by
 * email.
 */
function scrubSpanData<T extends Record<string, unknown>>(data: T): T {
  const kept: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    const header = HEADER_ATTRIBUTE.exec(key)?.[1];
    if (CLIENT_ADDRESS_ATTRIBUTES.has(key)) {
      continue;
    }
    if (header === undefined) {
      kept[key] = value;
    } else if (KEPT_HEADER_ATTRIBUTES.has(header)) {
      kept[key] =
        header === 'referer' && typeof value === 'string'
          ? redactUrl(value)
          : value;
    }
  }
  return redactUrlFields(kept) as T;
}

const UPSTREAM_BODY = / Body: [\s\S]*$/;
// An error page from the edge, which the AI SDK's chat transport throws as the
// error's whole message: about 50 KB of markup that names no failure.
const HTML_BODY = /^\s*<(?:!doctype\s+html|html)\b[\s\S]*$/i;
const URL_WITH_QUERY = /\S*\?\S*/g;
// The leading group starts a match only where a local part starts, so a long
// run with no address in it is scanned once, not once per character. A
// group rather than a lookbehind, which older Safari cannot parse.
const EMAIL = /(^|[^\w.%+-])[\w.%+-]+@[\w-]+(?:\.[\w-]+)+/g;

/**
 * Free text with any quoted upstream response, query value and email address
 * removed. Most lines hold no `@`, so they skip the email pattern.
 */
export function scrubMessage(text: string): string {
  const scrubbed = text
    .replace(UPSTREAM_BODY, '')
    .replace(HTML_BODY, '[HTML response body]')
    .replace(URL_WITH_QUERY, redactUrl);
  return scrubbed.includes('@')
    ? scrubbed.replace(EMAIL, '$1[email]')
    : scrubbed;
}

function scrubExtra(extra: Record<string, unknown>): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(extra)) {
    scrubbed[key] = typeof value === 'string' ? scrubMessage(value) : value;
  }
  return scrubbed;
}

/**
 * `beforeSendLog`. A server log line carries the same free text as an event's
 * message and `extra`, so its message and every string attribute go through
 * `scrubMessage`. `sendServerLog` has already turned each object field into a
 * string, so nothing nested escapes.
 */
export function scrubLog(log: Log): Log {
  return {
    ...log,
    message: scrubMessage(String(log.message)),
    attributes: log.attributes && scrubExtra(log.attributes),
  };
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category === 'console') {
    return null;
  }

  const scrubbed = { ...breadcrumb };

  if (scrubbed.category?.startsWith('ui.') && scrubbed.message) {
    scrubbed.message = scrubbed.message.replace(LABEL_ATTRIBUTES, '[$1]');
  }

  if (scrubbed.data) {
    scrubbed.data = redactUrlFields(scrubbed.data);
  }

  return scrubbed;
}

/**
 * Keeps the request's method, path and the headers in `KEPT_HEADERS`, and
 * drops the body, which is a form a recruiter filled in, the cookies and every
 * other header. Redacts query values wherever an event of any type carries a
 * URL: the request, the request path of a request error, the transaction
 * name, each span and a replay's `urls`. Strips a quoted upstream response
 * from the exception values, the message and each string `extra`.
 *
 * A `replay_event` and a `feedback` event never pass through `beforeSend`;
 * `scrubUnfilteredEvent` routes them here.
 */
export function scrubEvent<T extends Event>(event: T): T {
  const scrubbed: T & { urls?: unknown } = { ...event };

  if (event.exception?.values) {
    scrubbed.exception = {
      ...event.exception,
      values: event.exception.values.map((exception) => ({
        ...exception,
        value: exception.value && scrubMessage(exception.value),
      })),
    };
  }

  if (event.message) {
    scrubbed.message = scrubMessage(event.message);
  }

  if (event.extra) {
    scrubbed.extra = scrubExtra(event.extra);
  }

  const { request } = event;
  if (request) {
    const headers: Record<string, string> = {};
    for (const [name, value] of Object.entries(request.headers ?? {})) {
      const lower = name.toLowerCase();
      if (KEPT_HEADERS.has(lower)) {
        headers[name] = lower === 'referer' ? redactUrl(value) : value;
      }
    }
    scrubbed.request = {
      ...request,
      url: request.url && redactUrl(request.url),
      headers,
      data: undefined,
      cookies: undefined,
      query_string: undefined,
    };
  }

  if (event.transaction) {
    scrubbed.transaction = redactUrl(event.transaction);
  }

  const trace = event.contexts?.trace;
  if (trace?.data) {
    scrubbed.contexts = {
      ...scrubbed.contexts,
      trace: { ...trace, data: scrubSpanData(trace.data) },
    };
  }

  // `captureRequestError` records the request's path, query string included.
  const nextjs = event.contexts?.nextjs;
  if (typeof nextjs?.request_path === 'string') {
    scrubbed.contexts = {
      ...scrubbed.contexts,
      nextjs: { ...nextjs, request_path: redactUrl(nextjs.request_path) },
    };
  }

  if (event.spans) {
    scrubbed.spans = event.spans.map((span) => ({
      ...span,
      description: span.description && redactUrl(span.description),
      data: span.data && scrubSpanData(span.data),
    }));
  }

  // A feedback event holds the page it was sent from.
  const feedback = event.contexts?.feedback;
  if (typeof feedback?.url === 'string') {
    scrubbed.contexts = {
      ...scrubbed.contexts,
      feedback: { ...feedback, url: redactUrl(feedback.url) },
    };
  }

  // A replay event lists every page the session visited.
  if (Array.isArray(scrubbed.urls)) {
    scrubbed.urls = scrubbed.urls.map((url) =>
      typeof url === 'string' ? redactUrl(url) : url
    );
  }

  return scrubbed;
}

/**
 * The browser's event processor, for the event types the SDK sends without
 * calling `beforeSend`. A replay event lists the pages its session visited. A
 * feedback event carries the page URL twice: the reporter's
 * `contexts.feedback.url`, and the `request.url` that `httpContextIntegration`
 * copies from `window.location`.
 */
export function scrubUnfilteredEvent<T extends Event>(event: T): T {
  return event.type === 'replay_event' || event.type === 'feedback'
    ? scrubEvent(event)
    : event;
}

/**
 * The starts of the message every server render failure carries. React's
 * production build throws the minified form, error #441; Sentry expands it to
 * the full text only after ingest, so the browser's `beforeSend` sees the code.
 */
const SERVER_RENDER_MESSAGES = [
  'An error occurred in the Server Components render',
  'Minified React error #441;',
];

/**
 * The browser client's `beforeSend`: files an unhandled `AbortError` as one
 * warning issue, then scrubs every event. TanStack Query aborts a query's
 * fetch on unmount and on a superseding refetch, and its own code handles the
 * rejection. One that reaches the global handler went through a `fetch`
 * wrapper outside this app, such as a browser extension's. The match is the
 * error type alone, so a `controller.abort()` timeout or a non-fetch abort
 * lands in the same issue; one countable issue that alert rules skip keeps
 * them visible without paging anyone.
 *
 * A stylesheet that fails to load is filed the same way, as one warning
 * issue. React gives each hoisted `<link rel="stylesheet">` a promise that
 * rejects with the link's `error` Event and that nothing awaits, so a CSS
 * chunk lost to the network or to deploy skew reaches the global handler as
 * "Event `Event` (type=error) captured as promise rejection". The SDK's event
 * carries no URL, so the link's `href` goes on the `stylesheet.href` tag.
 *
 * A server render failure reaches an error boundary as Next's production
 * message, the same text whatever the cause. `onRequestError` already reports
 * the server's own exception, with the cause and the same `digest`, so the
 * boundary's copy is filed as a warning, one issue per route: issue alert
 * rules triage the server's issue, and `digest:<value>` still joins the two.
 */
export function browserBeforeSend<T extends Event>(
  event: T,
  hint?: EventHint
): T | null {
  // Both wrappers send a sanitized no-response warning and rethrow the original so
  // local error UI still runs. Neither that catch nor a global handler owns a second report.
  if (
    hint?.originalException instanceof Error &&
    reportedFetchNoResponseErrors.has(hint.originalException)
  ) {
    return null;
  }
  const values = event.exception?.values;
  const exception = values?.[values.length - 1];
  // Sentry's fetch instrumentation appends ` (host)` to all three messages
  // for absolute URLs in every browser, with enhanceFetchErrorMessages at its
  // default "always". Exact matching leaves those cross-origin failures alone;
  // relative URLs fail Sentry's URL parse and keep the bare message.
  if (
    exception?.type === 'TypeError' &&
    FETCH_NO_RESPONSE_MESSAGES.includes(exception.value ?? '')
  ) {
    const captureSite = event.tags?.feature ?? exception.mechanism?.type;
    const context = fetchNoResponseContext(hint?.originalException);
    exception.type = FETCH_NO_RESPONSE;
    exception.value = 'request failed (no wrapper)';
    event.level = context.level;
    event.fingerprint = context.fingerprint;
    event.tags = { ...event.tags, ...context.tags, capture_site: captureSite };
  }
  const unhandledRejection =
    exception?.mechanism?.type ===
    'auto.browser.global_handlers.onunhandledrejection';
  const link = unhandledRejection ? failedLink(hint) : undefined;
  if (link?.rel === 'stylesheet' && staticAssetPath(link.href)) {
    return null;
  }
  if (unhandledRejection && exception?.type === 'AbortError') {
    event.level = 'warning';
    event.fingerprint = ['browser', 'unhandled-abort-error'];
  } else if (link) {
    event.level = 'warning';
    event.fingerprint = ['browser', 'stylesheet-load-error'];
    event.tags = { ...event.tags, 'stylesheet.href': redactUrl(link.href) };
  } else if (
    event.tags?.digest &&
    SERVER_RENDER_MESSAGES.some((start) => exception?.value?.startsWith(start))
  ) {
    event.level = 'warning';
    event.fingerprint = [
      'browser',
      'server-render',
      event.transaction ?? 'unknown-route',
    ];
  }
  return scrubEvent(event);
}

/** The `<link>` whose `error` Event a rejection carries, if it carries one. */
function failedLink(hint: EventHint | undefined): HTMLLinkElement | undefined {
  const reason = hint?.originalException;
  return reason instanceof globalThis.Event &&
    reason.type === 'error' &&
    reason.target instanceof HTMLLinkElement
    ? reason.target
    : undefined;
}

/**
 * Replay's `beforeAddRecordingEvent`. The SDK passes only its own custom
 * events here: a navigation, a fetch or a page load as a `performanceSpan`
 * whose description is the URL, and each breadcrumb again. The DOM recorder's
 * own events, including the page `href` in each snapshot's meta event, never
 * reach this hook.
 *
 * Replay records its own click and keypress breadcrumbs, which never pass
 * through the client's `beforeBreadcrumb`. Their `message` is the element
 * path of the live DOM, `aria-label` included, so every breadcrumb payload
 * goes through `scrubBreadcrumb` here; one it drops is dropped from the
 * recording too.
 */
export function scrubRecordingEvent<T>(event: T): T | null {
  const data = (event as { data?: { tag?: unknown; payload?: unknown } }).data;
  const payload = data?.payload as
    { description?: unknown; data?: Record<string, unknown> } | undefined;
  if (!data || !payload || typeof payload !== 'object') {
    return event;
  }

  if (data.tag === 'breadcrumb') {
    const breadcrumb = scrubBreadcrumb(payload as Breadcrumb);
    return breadcrumb && { ...event, data: { ...data, payload: breadcrumb } };
  }

  const scrubbed = { ...payload };
  if (typeof scrubbed.description === 'string') {
    scrubbed.description = redactUrl(scrubbed.description);
  }
  if (scrubbed.data && typeof scrubbed.data === 'object') {
    scrubbed.data = redactUrlFields(scrubbed.data);
  }
  return { ...event, data: { ...data, payload: scrubbed } };
}
